import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Github,
  Gitlab,
  FolderOpen,
  Cloud,
  GitBranch,
  Check,
  ChevronRight,
  ChevronLeft,
  Box,
  Network,
  ShieldCheck,
  Ticket,
  Camera,
  Plus,
  Server,
  KeyRound,
  Gauge,
  MessageSquare,
  Calendar,
  FileCode2,
  BookOpen,
  CircleDot,
  Loader2,
  ArrowRight,
  HardDrive,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { APPS } from "@/data/apps";
import { isEnabled } from "@/lib/entitlements";
import { byoncPolicyLabel } from "@/data/workspaces";
import type { CodeWorkspace, GitProvider, WsEnv, WsSnapshot } from "@/types";

const accent = APPS.workspaces.accent;

type SourceId = GitProvider | "local" | "cloud";

const SOURCES: { id: SourceId; name: string; icon: any; desc: string }[] = [
  { id: "github", name: "GitHub", icon: Github, desc: "Connect a repository from GitHub" },
  { id: "gitlab", name: "GitLab", icon: Gitlab, desc: "Self-managed or gitlab.com" },
  { id: "bitbucket", name: "Bitbucket", icon: Box, desc: "Bitbucket Cloud repositories" },
  { id: "local", name: "Local folder", icon: FolderOpen, desc: "Mount a folder from your machine" },
  { id: "cloud", name: "Cloud storage", icon: Cloud, desc: "Start from files in an S3 bucket" },
];

/** Deterministic accent per repo name — presentation only, not data. */
const REPO_ACCENTS = ["#3b82f6", "#f97316", "#22c55e", "#a855f7", "#10b981", "#f43f5e", "#06b6d4", "#eab308"];
const accentFor = (name: string) =>
  REPO_ACCENTS[[...name].reduce((n, c) => n + c.charCodeAt(0), 0) % REPO_ACCENTS.length];

const BRANCH_MODES = [
  { id: "existing", label: "Use existing branch", icon: GitBranch, hint: "Check out a branch that already exists" },
  { id: "new", label: "Create new branch", icon: Plus, hint: "Branch off the default base branch" },
  { id: "jira", label: "Create from Jira ticket", icon: Ticket, hint: "Pick a ticket — the branch is named after it" },
  { id: "task", label: "Create from task", icon: CircleDot, hint: "Pick a Hive task to work on in this workspace" },
  { id: "snapshot", label: "From workspace snapshot", icon: Camera, hint: "Fork a saved snapshot into a new branch" },
];

const CONTEXT_LINKS = [
  { id: "jira", label: "Jira project", icon: Ticket },
  { id: "slack", label: "Slack channel", icon: MessageSquare },
  { id: "calendar", label: "Calendar focus blocks", icon: Calendar },
  { id: "issues", label: "GitHub issues", icon: CircleDot },
  { id: "prs", label: "Pull requests", icon: GitBranch },
  { id: "docs", label: "Documentation", icon: BookOpen },
  { id: "specs", label: "API specs", icon: FileCode2 },
];

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

const titleize = (s: string) => s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const lastSegment = (p: string) => p.replace(/\/+$/, "").split("/").filter(Boolean).pop() ?? "";

function OptionCard({
  active,
  onClick,
  icon: Icon,
  title,
  desc,
  right,
}: {
  active: boolean;
  onClick: () => void;
  icon?: any;
  title: string;
  desc?: string;
  right?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border p-3 text-left transition"
      style={{
        borderColor: active ? accent : "hsl(var(--border))",
        background: active ? `${accent}14` : "hsl(var(--surface-2) / 0.4)",
      }}
    >
      {Icon && (
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-lg"
          style={{ background: active ? accent : "hsl(var(--surface-2))", color: active ? "#000" : "var(--text-1)" }}
        >
          <Icon size={16} />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-app">{title}</div>
        {desc && <div className="truncate text-xs text-app-2">{desc}</div>}
      </div>
      {right}
      {active && !right && (
        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full" style={{ background: accent }}>
          <Check size={12} className="text-black" />
        </span>
      )}
    </button>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!on)}
      className="grid h-5 w-9 place-items-start rounded-full p-0.5 transition"
      style={{ background: on ? accent : "hsl(var(--surface-2))" }}
    >
      <span
        className="h-4 w-4 rounded-full bg-white shadow transition-transform"
        style={{ transform: on ? "translateX(16px)" : "translateX(0)" }}
      />
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-app bg-surface-2/40 p-3">
      <label className="text-[11px] uppercase tracking-wider text-app-2">{label}</label>
      {children}
    </div>
  );
}

/** Inline provider auth: the place to paste a personal access token for
 *  GitHub / GitLab / Bitbucket without leaving the wizard. */
function ProviderAuth({ provider }: { provider: GitProvider }) {
  const integrations = useHive((s) => s.integrations);
  const connectIntegration = useHive((s) => s.connectIntegration);
  const installIntegration = useHive((s) => s.installIntegration);
  const openApp = useHive((s) => s.openApp);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const name = SOURCES.find((s) => s.id === provider)?.name ?? provider;
  const integration = integrations.find((i) => i.id === provider);
  const connected = integration?.status === "connected";

  async function connect() {
    setBusy(true);
    const credentials = { token: token.trim() };
    const outcome = integration
      ? await connectIntegration(provider, credentials)
      : await installIntegration(provider, credentials);
    setBusy(false);
    if (outcome.ok) setToken("");
  }

  return (
    <div className="mt-3 rounded-xl border border-app bg-surface-2/40 p-3">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-app-2">
          <KeyRound size={11} /> {name} access
        </span>
        <button
          onClick={() => openApp("integrations")}
          className="flex items-center gap-1 text-[11px] text-app-2 transition hover:text-app"
        >
          Integration Center <ArrowRight size={11} />
        </button>
      </div>
      {connected ? (
        <div className="mt-2 flex items-center gap-2 text-xs text-app-1">
          <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500/15">
            <Check size={12} className="text-emerald-500" />
          </span>
          Connected — an authorization token is on file for your org.
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={`Paste a ${name} personal access token`}
              className="w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
            />
            <button
              disabled={busy || !token.trim()}
              onClick={() => void connect()}
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold text-black transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: accent }}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Connect
            </button>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-app-2">
            {name} requires an authorization token before Hive can list or clone repositories. The token is
            stored server-side, verified against the {name} API, and never shown again. You can continue
            without connecting, but the workspace won't be able to sync until you do.
          </p>
        </>
      )}
    </div>
  );
}

export function WorkspaceWizard() {
  const open = useHive((s) => s.wsWizardOpen);
  const setOpen = useHive((s) => s.setWsWizard);
  const createWorkspace = useHive((s) => s.createWorkspace);
  const entitlements = useHive((s) => s.entitlements);
  const tasks = useHive((s) => s.tasks);
  const workspaces = useHive((s) => s.codeWorkspaces);
  const byoncPolicies = useHive((s) => s.byoncPolicies);
  const openApp = useHive((s) => s.openApp);

  const aresEnabled = isEnabled(entitlements, "ares");
  const garlicEnabled = isEnabled(entitlements, "garlic");
  const byoncEnabled = isEnabled(entitlements, "byonc");

  const [step, setStep] = useState(0);
  const [source, setSource] = useState<SourceId>("github");
  const [repoName, setRepoName] = useState("");
  const [baseBranch, setBaseBranch] = useState("main");
  const [branchMode, setBranchMode] = useState("existing");
  const [branchName, setBranchName] = useState("feature/new-workspace");
  const [existingBranch, setExistingBranch] = useState("");
  const [pickedTicketId, setPickedTicketId] = useState<string | null>(null);
  const [pickedTaskId, setPickedTaskId] = useState<string | null>(null);
  const [pickedSnapshotKey, setPickedSnapshotKey] = useState<string | null>(null);
  const [localPath, setLocalPath] = useState("");
  const [bucket, setBucket] = useState("");
  const [bucketPrefix, setBucketPrefix] = useState("");
  const [customName, setCustomName] = useState("");
  const [env, setEnv] = useState<WsEnv>("cloud");
  const [persistent, setPersistent] = useState(true);
  const [autoVerify, setAutoVerify] = useState(aresEnabled);
  const [aresOn, setAresOn] = useState(aresEnabled);
  const [garlicOn, setGarlicOn] = useState(garlicEnabled);
  const [byoncMode, setByoncMode] = useState("cost-optimized");
  const [compute, setCompute] = useState("4 vCPU · 16 GB");
  const [secretsProfile, setSecretsProfile] = useState("");
  const [links, setLinks] = useState<string[]>(["jira", "slack", "issues"]);

  const isGit = source !== "local" && source !== "cloud";

  // Steps depend on the source: git hosts need repository + branch, local
  // folders and S3 buckets have their own details step instead.
  const steps = useMemo(() => {
    const base: { key: string; label: string }[] = [{ key: "source", label: "Source" }];
    if (isGit) base.push({ key: "repo", label: "Repository" }, { key: "branch", label: "Branch" });
    else base.push({ key: "details", label: source === "local" ? "Local folder" : "Cloud storage" });
    base.push({ key: "env", label: "Environment" }, { key: "context", label: "Context" }, { key: "review", label: "Review" });
    return base;
  }, [isGit, source]);
  const stepKey = steps[step]?.key ?? "source";

  // Branch-mode pickers: Jira tickets, open Hive tasks, saved snapshots.
  const jiraTickets = useMemo(() => tasks.filter((t) => t.source === "jira"), [tasks]);
  const openTasks = useMemo(() => tasks.filter((t) => t.status !== "done"), [tasks]);
  const snapshotChoices = useMemo(
    () =>
      workspaces.flatMap((ws) =>
        ws.snapshots.map((sn) => ({ key: `${ws.id}:${sn.id}`, ws, sn })),
      ),
    [workspaces],
  );

  // Re-align entitlement-dependent defaults each time the wizard opens (the
  // session payload may have arrived after mount).
  useEffect(() => {
    if (!open) return;
    if (!aresEnabled) {
      setAresOn(false);
      setAutoVerify(false);
    }
    if (!garlicEnabled) setGarlicOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The environment type follows the source until changed by hand.
  useEffect(() => {
    setEnv(source === "local" ? "local" : "cloud");
  }, [source]);

  function reset() {
    setStep(0);
    setSource("github");
    setRepoName("");
    setBaseBranch("main");
    setBranchMode("existing");
    setBranchName("feature/new-workspace");
    setExistingBranch("");
    setPickedTicketId(null);
    setPickedTaskId(null);
    setPickedSnapshotKey(null);
    setLocalPath("");
    setBucket("");
    setBucketPrefix("");
    setCustomName("");
    setSecretsProfile("");
    setByoncMode("cost-optimized");
    setLinks(["jira", "slack", "issues"]);
  }
  function close() {
    setOpen(false);
    setTimeout(reset, 250);
  }
  function toggleLink(id: string) {
    setLinks((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  }

  const linkedTask =
    branchMode === "jira"
      ? jiraTickets.find((t) => t.id === pickedTicketId)
      : branchMode === "task"
        ? openTasks.find((t) => t.id === pickedTaskId)
        : undefined;
  const snapChoice = branchMode === "snapshot" ? snapshotChoices.find((c) => c.key === pickedSnapshotKey) : undefined;

  const shortRepo = repoName.replace(/^.*\//, "");
  const defaultShortName = isGit ? shortRepo : source === "local" ? lastSegment(localPath) : bucket;
  const defaultSecrets = `${defaultShortName || "workspace"}-default`;

  function stepValid(key: string): boolean {
    switch (key) {
      case "repo":
        return repoName.trim().length > 0 && baseBranch.trim().length > 0;
      case "details":
        return source === "local" ? localPath.trim().length > 0 : bucket.trim().length > 0;
      case "branch":
        if (branchMode === "existing") return true;
        if (branchMode === "jira" && jiraTickets.length > 0 && !pickedTicketId) return false;
        if (branchMode === "task" && openTasks.length > 0 && !pickedTaskId) return false;
        if (branchMode === "snapshot" && snapshotChoices.length > 0 && !pickedSnapshotKey) return false;
        return branchName.trim().length > 0;
      default:
        return true;
    }
  }

  function finish() {
    const displayRepo = isGit
      ? repoName
      : source === "local"
        ? localPath
        : `s3://${bucket}${bucketPrefix ? `/${bucketPrefix.replace(/^\/+/, "")}` : ""}`;
    const base = snapChoice ? snapChoice.sn.branch : baseBranch;
    const branch = !isGit
      ? "—"
      : branchMode === "existing"
        ? existingBranch.trim() || baseBranch
        : branchName;
    const name =
      customName.trim() ||
      (linkedTask
        ? linkedTask.title
        : snapChoice
          ? `${snapChoice.sn.name} (fork)`
          : titleize(defaultShortName || "New Workspace"));

    const timeline = snapChoice
      ? [{ label: "Workspace created from snapshot", detail: `${snapChoice.sn.name} · ${snapChoice.ws.name}`, time: "now" }]
      : linkedTask
        ? [{ label: "Workspace created", detail: `${linkedTask.id} · ${linkedTask.title}`, time: "now" }]
        : [{ label: "Workspace created", detail: isGit ? `${repoName} · ${branch}` : displayRepo, time: "now" }];

    const partial: Partial<CodeWorkspace> = {
      name,
      repo: displayRepo,
      provider: isGit ? source : source === "local" ? "local" : "s3",
      branch,
      baseBranch: isGit ? base : "—",
      product: "ares",
      env,
      lang: "—",
      accent: accentFor(displayRepo),
      garlic: garlicOn ? "active" : "none",
      byonc: byoncMode,
      compute,
      persistent,
      autoVerify,
      aresAgents: aresOn,
      openTasks: linkedTask ? 1 : 0,
      integrations: [...(isGit ? [source] : []), ...links].filter((v, i, a) => a.indexOf(v) === i),
      secretsProfile: secretsProfile.trim() || defaultSecrets,
      timeline,
      snapshots: snapChoice ? [snapChoice.sn as WsSnapshot] : [],
      ...(source === "local" ? { localPath } : {}),
      ...(source === "cloud" ? { s3Bucket: bucket, ...(bucketPrefix ? { s3Prefix: bucketPrefix } : {}) } : {}),
    };
    createWorkspace(partial, linkedTask ? { linkTaskId: linkedTask.id } : undefined);
    close();
  }

  const last = step === steps.length - 1;
  const reviewRepo = isGit
    ? repoName
    : source === "local"
      ? localPath
      : `s3://${bucket}${bucketPrefix ? `/${bucketPrefix.replace(/^\/+/, "")}` : ""}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="absolute inset-0 z-[70] flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
            className="relative z-10 flex h-[560px] w-full max-w-3xl overflow-hidden rounded-2xl border border-app-strong glass-strong shadow-window"
          >
            {/* Stepper rail */}
            <div className="hidden w-52 shrink-0 flex-col border-r border-app bg-surface-2/30 p-5 sm:flex">
              <div className="mb-5 flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg text-black" style={{ background: accent }}>
                  <Plus size={16} />
                </div>
                <div className="text-sm font-semibold text-app">New Workspace</div>
              </div>
              <div className="relative space-y-1">
                <div className="absolute bottom-3 left-[13px] top-3 w-px bg-app" />
                {steps.map((s, i) => {
                  const done = i < step;
                  const cur = i === step;
                  return (
                    <button
                      key={s.key}
                      onClick={() => i <= step && setStep(i)}
                      className="relative flex w-full items-center gap-3 rounded-lg px-1.5 py-2 text-left"
                    >
                      <span
                        className="relative z-10 grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border-2 text-[11px] font-semibold transition"
                        style={{
                          background: done || cur ? accent : "hsl(var(--surface))",
                          borderColor: done || cur ? accent : "hsl(var(--border-strong))",
                          color: done || cur ? "#000" : "var(--text-2)",
                        }}
                      >
                        {done ? <Check size={13} /> : i + 1}
                      </span>
                      <span className={`text-sm ${cur ? "font-medium text-app" : "text-app-2"}`}>{s.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-auto text-[11px] leading-relaxed text-app-2">
                The workspace is created in your organization and synced to the Hive API.
              </div>
            </div>

            {/* Content */}
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center justify-between border-b border-app px-5 py-3.5">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-app-2">
                    Step {step + 1} of {steps.length}
                  </div>
                  <div className="text-sm font-semibold text-app">{steps[step].label}</div>
                </div>
                <button onClick={close} className="rounded-lg px-2 py-1 text-xs text-app-2 hover:bg-surface-2">
                  Cancel
                </button>
              </div>

              <div className="flex-1 overflow-y-auto no-scrollbar p-5">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={stepKey}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* A — Source */}
                    {stepKey === "source" && (
                      <div className="space-y-2">
                        <p className="mb-3 text-xs text-app-2">Where does this workspace come from?</p>
                        {SOURCES.map((s) => (
                          <OptionCard
                            key={s.id}
                            active={source === s.id}
                            onClick={() => setSource(s.id)}
                            icon={s.icon}
                            title={s.name}
                            desc={s.desc}
                          />
                        ))}
                        {isGit && <ProviderAuth provider={source as GitProvider} />}
                      </div>
                    )}

                    {/* B (git) — Repository */}
                    {stepKey === "repo" && (
                      <div className="space-y-3">
                        <p className="text-xs text-app-2">
                          Which repository should this workspace clone? Use the{" "}
                          <span className="font-medium text-app-1">owner/name</span> form from your{" "}
                          {SOURCES.find((s) => s.id === source)?.name ?? "provider"} account.
                        </p>
                        <Field label="Repository">
                          <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-app bg-surface px-3 py-2 focus-within:border-app-strong">
                            <GitBranch size={14} className="shrink-0 text-app-2" />
                            <input
                              autoFocus
                              value={repoName}
                              onChange={(e) => setRepoName(e.target.value)}
                              placeholder="acme/payments-api"
                              className="w-full bg-transparent font-mono text-sm text-app outline-none placeholder:text-app-2"
                            />
                          </div>
                        </Field>
                        <Field label="Default branch">
                          <input
                            value={baseBranch}
                            onChange={(e) => setBaseBranch(e.target.value)}
                            placeholder="main"
                            className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 font-mono text-sm text-app outline-none focus:border-app-strong"
                          />
                        </Field>
                      </div>
                    )}

                    {/* B (local) / B (cloud) — Source details */}
                    {stepKey === "details" && source === "local" && (
                      <div className="space-y-3">
                        <p className="text-xs text-app-2">
                          Mount a folder from your machine. No repository or branch is needed — the workspace
                          wraps the files exactly as they are on disk.
                        </p>
                        <Field label="Folder path">
                          <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-app bg-surface px-3 py-2 focus-within:border-app-strong">
                            <FolderOpen size={14} className="shrink-0 text-app-2" />
                            <input
                              autoFocus
                              value={localPath}
                              onChange={(e) => setLocalPath(e.target.value)}
                              placeholder="~/dev/payments-api"
                              className="w-full bg-transparent font-mono text-sm text-app outline-none placeholder:text-app-2"
                            />
                          </div>
                        </Field>
                        <Field label="Workspace name (optional)">
                          <input
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder={localPath ? titleize(lastSegment(localPath)) : "Derived from the folder name"}
                            className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none focus:border-app-strong placeholder:text-app-2"
                          />
                        </Field>
                        <p className="rounded-lg border border-dashed border-app p-3 text-[11px] leading-relaxed text-app-2">
                          Live sync between this folder and the cloud IDE isn't wired up yet — the path is
                          recorded on the workspace so the IDE can mount it once sync ships.
                        </p>
                      </div>
                    )}
                    {stepKey === "details" && source === "cloud" && (
                      <div className="space-y-3">
                        <p className="text-xs text-app-2">
                          Start from files stored in object storage. No repository or branch is needed — the
                          bucket contents become the workspace's starting state.
                        </p>
                        <Field label="S3 bucket">
                          <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-app bg-surface px-3 py-2 focus-within:border-app-strong">
                            <HardDrive size={14} className="shrink-0 text-app-2" />
                            <input
                              autoFocus
                              value={bucket}
                              onChange={(e) => setBucket(e.target.value)}
                              placeholder="acme-dev-workspaces"
                              className="w-full bg-transparent font-mono text-sm text-app outline-none placeholder:text-app-2"
                            />
                          </div>
                        </Field>
                        <Field label="Key prefix (optional)">
                          <input
                            value={bucketPrefix}
                            onChange={(e) => setBucketPrefix(e.target.value)}
                            placeholder="teams/payments/env-1"
                            className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 font-mono text-sm text-app outline-none focus:border-app-strong placeholder:text-app-2"
                          />
                        </Field>
                        <Field label="Workspace name (optional)">
                          <input
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder={bucket ? titleize(bucket) : "Derived from the bucket name"}
                            className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none focus:border-app-strong placeholder:text-app-2"
                          />
                        </Field>
                      </div>
                    )}

                    {/* C (git) — Branch */}
                    {stepKey === "branch" && (
                      <div className="space-y-2">
                        <p className="mb-1 text-xs text-app-2">How should we set up the working branch?</p>
                        {BRANCH_MODES.map((b) => (
                          <OptionCard
                            key={b.id}
                            active={branchMode === b.id}
                            onClick={() => setBranchMode(b.id)}
                            icon={b.icon}
                            title={b.label}
                            desc={b.hint}
                          />
                        ))}

                        {branchMode === "existing" && (
                          <div className="mt-3">
                            <Field label="Branch to check out">
                              <input
                                value={existingBranch}
                                onChange={(e) => setExistingBranch(e.target.value)}
                                placeholder={baseBranch || "main"}
                                className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 font-mono text-sm text-app outline-none focus:border-app-strong placeholder:text-app-2"
                              />
                            </Field>
                          </div>
                        )}

                        {branchMode === "jira" && (
                          <div className="mt-3 space-y-2">
                            <div className="text-[11px] uppercase tracking-wider text-app-2">Pick a Jira ticket</div>
                            {jiraTickets.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-app p-3 text-xs text-app-2">
                                No Jira tickets in your task queue. Connect Jira in the Integration Center and
                                tickets will appear here — or name the branch manually below.
                              </div>
                            ) : (
                              <div className="max-h-44 space-y-1.5 overflow-y-auto no-scrollbar">
                                {jiraTickets.map((t) => (
                                  <OptionCard
                                    key={t.id}
                                    active={pickedTicketId === t.id}
                                    onClick={() => {
                                      setPickedTicketId(t.id);
                                      setBranchName(`${t.id.toLowerCase()}-${slugify(t.title)}`);
                                    }}
                                    icon={Ticket}
                                    title={`${t.id} — ${t.title}`}
                                    desc={`${t.priority} priority · ${t.status.replace(/-/g, " ")}`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {branchMode === "task" && (
                          <div className="mt-3 space-y-2">
                            <div className="text-[11px] uppercase tracking-wider text-app-2">Pick a task</div>
                            {openTasks.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-app p-3 text-xs text-app-2">
                                No open tasks. Create one in the Task Command Center and it will appear here —
                                or name the branch manually below.
                              </div>
                            ) : (
                              <div className="max-h-44 space-y-1.5 overflow-y-auto no-scrollbar">
                                {openTasks.map((t) => (
                                  <OptionCard
                                    key={t.id}
                                    active={pickedTaskId === t.id}
                                    onClick={() => {
                                      setPickedTaskId(t.id);
                                      setBranchName(`task/${t.id.toLowerCase()}-${slugify(t.title)}`);
                                    }}
                                    icon={CircleDot}
                                    title={`${t.id} — ${t.title}`}
                                    desc={`${t.source} · ${t.status.replace(/-/g, " ")}`}
                                  />
                                ))}
                              </div>
                            )}
                            <p className="text-[11px] leading-relaxed text-app-2">
                              The picked task is linked to the new workspace, so it shows up under "Linked
                              tasks" and can be sent to ARES from there.
                            </p>
                          </div>
                        )}

                        {branchMode === "snapshot" && (
                          <div className="mt-3 space-y-2">
                            <p className="rounded-lg border border-dashed border-app p-3 text-[11px] leading-relaxed text-app-2">
                              A snapshot is a saved image of a workspace's files and environment. Forking one
                              starts the new workspace from that exact state, on a fresh branch cut from the
                              branch the snapshot was taken on — nothing in the original workspace changes.
                            </p>
                            <div className="text-[11px] uppercase tracking-wider text-app-2">Pick a snapshot</div>
                            {snapshotChoices.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-app p-3 text-xs text-app-2">
                                No snapshots saved yet. Save one from a workspace's detail panel and it will
                                appear here — or name the branch manually below.
                              </div>
                            ) : (
                              <div className="max-h-40 space-y-1.5 overflow-y-auto no-scrollbar">
                                {snapshotChoices.map((c) => (
                                  <OptionCard
                                    key={c.key}
                                    active={pickedSnapshotKey === c.key}
                                    onClick={() => {
                                      setPickedSnapshotKey(c.key);
                                      setBranchName(`snapshot/${slugify(c.sn.name)}`);
                                    }}
                                    icon={Camera}
                                    title={c.sn.name}
                                    desc={`${c.ws.name} · ${c.sn.branch} · ${c.sn.createdAt} · ${c.sn.size}${c.sn.verified ? " · verified" : ""}`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {branchMode !== "existing" && (
                          <div className="mt-3">
                            <Field label="Branch name">
                              <input
                                value={branchName}
                                onChange={(e) => setBranchName(e.target.value)}
                                className="mt-1.5 w-full rounded-lg border border-app bg-surface px-3 py-2 font-mono text-sm text-app outline-none focus:border-app-strong"
                              />
                            </Field>
                          </div>
                        )}
                      </div>
                    )}

                    {/* D — Environment */}
                    {stepKey === "env" && (
                      <div className="space-y-4">
                        <div>
                          <div className="mb-2 text-[11px] uppercase tracking-wider text-app-2">Linked product</div>
                          <div
                            className="flex items-center gap-3 rounded-xl border p-3"
                            style={{ borderColor: APPS.ares.accent, background: `${APPS.ares.accent}14` }}
                          >
                            <span
                              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg"
                              style={{ background: `${APPS.ares.accent}22`, color: APPS.ares.accent }}
                            >
                              <ShieldCheck size={16} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 text-sm font-medium text-app">
                                ARES
                                {!aresEnabled && (
                                  <span
                                    className="rounded-full px-1.5 py-px text-[8px] font-bold uppercase tracking-wide"
                                    style={{ background: `${APPS.ares.accent}22`, color: APPS.ares.accent }}
                                  >
                                    Early Access
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-app-2">
                                Workspaces are files + an IDE, so they always link to ARES — the IDE product.
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="mb-2 text-[11px] uppercase tracking-wider text-app-2">Environment type</div>
                          <div className="grid grid-cols-4 gap-2">
                            {(["local", "cloud", "vpc", "ephemeral"] as WsEnv[]).map((e) => (
                              <button
                                key={e}
                                onClick={() => setEnv(e)}
                                className="rounded-lg border py-2 text-xs font-medium capitalize transition"
                                style={{
                                  borderColor: env === e ? accent : "hsl(var(--border))",
                                  background: env === e ? `${accent}14` : "transparent",
                                  color: env === e ? "var(--text-0)" : "var(--text-1)",
                                }}
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded-xl border border-app bg-surface-2/40 p-3">
                            <div className="flex items-center gap-1.5 text-[11px] text-app-2"><Server size={12} /> Runtime image (default)</div>
                            <div className="mt-1 text-sm font-medium text-app">ubuntu-24.04 · node 20</div>
                          </div>
                          <div className="rounded-xl border border-app bg-surface-2/40 p-3">
                            <div className="flex items-center gap-1.5 text-[11px] text-app-2"><Box size={12} /> Package manager</div>
                            <div className="mt-1 text-sm font-medium text-app">Auto-detect</div>
                          </div>
                          <div className="rounded-xl border border-app bg-surface-2/40 p-3">
                            <div className="flex items-center gap-1.5 text-[11px] text-app-2"><KeyRound size={12} /> Secrets profile</div>
                            <input
                              value={secretsProfile}
                              onChange={(e) => setSecretsProfile(e.target.value)}
                              placeholder={defaultSecrets}
                              className="mt-1 w-full rounded bg-transparent font-mono text-sm font-medium text-app outline-none placeholder:text-app-2"
                            />
                          </div>
                          <div className="rounded-xl border border-app bg-surface-2/40 p-3">
                            <div className="flex items-center gap-1.5 text-[11px] text-app-2"><Gauge size={12} /> Compute size</div>
                            <select
                              value={compute}
                              onChange={(e) => setCompute(e.target.value)}
                              className="mt-1 w-full rounded bg-transparent text-sm font-medium text-app outline-none"
                            >
                              <option>2 vCPU · 8 GB</option>
                              <option>4 vCPU · 16 GB</option>
                              <option>8 vCPU · 32 GB</option>
                            </select>
                          </div>
                        </div>

                        <div className="space-y-2 rounded-xl border border-app bg-surface-2/40 p-3">
                          {[
                            { label: "Persistent workspace", sub: "Keep state between sessions", on: persistent, set: setPersistent, locked: false },
                            { label: "Automatic verification", sub: aresEnabled ? "ARES verifies every change" : "Requires ARES · Early Access", on: autoVerify, set: setAutoVerify, locked: !aresEnabled },
                            { label: "Enable ARES agents", sub: aresEnabled ? "Autonomous implementation & proofs" : "Requires ARES · Early Access", on: aresOn, set: setAresOn, locked: !aresEnabled },
                            { label: "Garlic specialization memory", sub: garlicEnabled ? "Specialize agents to this repo" : "Requires GARLIC · Early Access", on: garlicOn, set: setGarlicOn, locked: !garlicEnabled },
                          ].map((row) => (
                            <div key={row.label} className="flex items-center justify-between" style={{ opacity: row.locked ? 0.55 : 1 }}>
                              <div>
                                <div className="text-sm text-app">{row.label}</div>
                                <div className="text-[11px] text-app-2">{row.sub}</div>
                              </div>
                              <Toggle on={row.on && !row.locked} onChange={(v) => !row.locked && row.set(v)} />
                            </div>
                          ))}
                          <div className="border-t border-app pt-2" style={{ opacity: byoncEnabled ? 1 : 0.55 }}>
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm text-app">BYONC routing</div>
                                <div className="text-[11px] text-app-2">
                                  {byoncEnabled ? "Inference routing policy" : "Requires BYONC · Early Access"}
                                </div>
                              </div>
                              <select
                                value={byoncMode}
                                disabled={!byoncEnabled}
                                onChange={(e) => setByoncMode(e.target.value)}
                                className="max-w-[180px] rounded-lg border border-app bg-surface px-2 py-1 text-xs text-app outline-none disabled:cursor-not-allowed"
                              >
                                {byoncPolicies.map((p) => (
                                  <option key={p.id} value={p.id} title={p.description}>
                                    {p.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {byoncEnabled && (
                              <button
                                onClick={() => openApp("byonc")}
                                className="mt-1.5 flex items-center gap-1 text-[11px] text-app-2 transition hover:text-app"
                              >
                                <Network size={11} /> Policies come from your BYONC console — manage them there{" "}
                                <ArrowRight size={11} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* E — Context */}
                    {stepKey === "context" && (
                      <div>
                        <p className="mb-3 text-xs text-app-2">Link the context Hive should keep in sync with this workspace.</p>
                        <div className="grid grid-cols-2 gap-2">
                          {CONTEXT_LINKS.map((c) => {
                            const on = links.includes(c.id);
                            return (
                              <button
                                key={c.id}
                                onClick={() => toggleLink(c.id)}
                                className="flex items-center gap-2.5 rounded-xl border p-3 text-left transition"
                                style={{
                                  borderColor: on ? accent : "hsl(var(--border))",
                                  background: on ? `${accent}14` : "transparent",
                                }}
                              >
                                <c.icon size={16} style={{ color: on ? accent : "var(--text-2)" }} />
                                <span className="flex-1 text-sm text-app">{c.label}</span>
                                <span
                                  className="grid h-4 w-4 place-items-center rounded border"
                                  style={{ background: on ? accent : "transparent", borderColor: on ? accent : "hsl(var(--border-strong))" }}
                                >
                                  {on && <Check size={11} className="text-black" />}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* F — Review */}
                    {stepKey === "review" && (
                      <div className="space-y-3">
                        <div className="rounded-xl border border-app bg-surface-2/40 p-4">
                          <div className="flex items-center gap-3">
                            <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${accentFor(reviewRepo)}22`, color: accentFor(reviewRepo) }}>
                              {isGit ? <GitBranch size={20} /> : source === "local" ? <FolderOpen size={20} /> : <Cloud size={20} />}
                            </span>
                            <div className="min-w-0">
                              <div className="truncate text-base font-semibold text-app">{reviewRepo}</div>
                              <div className="truncate font-mono text-xs text-app-2">
                                {isGit
                                  ? branchMode === "existing"
                                    ? existingBranch.trim() || baseBranch
                                    : branchName
                                  : source === "local"
                                    ? "local folder"
                                    : "object storage"}
                              </div>
                            </div>
                            <span className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ background: `${APPS.ares.accent}22` }}>
                              <ShieldCheck size={16} style={{ color: APPS.ares.accent }} />
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {[
                            ["Source", SOURCES.find((s) => s.id === source)?.name],
                            ["Environment", env],
                            ["Compute", compute],
                            ["Secrets profile", secretsProfile.trim() || defaultSecrets],
                            ["Persistence", persistent ? "Persistent" : "Ephemeral"],
                            ["Auto-verify", autoVerify ? "On" : "Off"],
                            ["ARES agents", aresOn ? "Enabled" : "Off"],
                            ["Garlic", garlicOn ? "Active" : "Off"],
                            ["BYONC", byoncPolicyLabel(byoncMode, byoncPolicies)],
                            ...(linkedTask ? [["Linked task", linkedTask.id]] : []),
                            ...(snapChoice ? [["From snapshot", snapChoice.sn.name]] : []),
                            ["Linked context", `${links.length} sources`],
                          ].map(([k, v]) => (
                            <div key={k as string} className="flex items-center justify-between gap-2 rounded-lg border border-app bg-surface-2/30 px-3 py-2">
                              <span className="shrink-0 text-xs text-app-2">{k}</span>
                              <span className="truncate text-xs font-medium capitalize text-app" title={String(v)}>{v}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {links.map((l) => {
                            const c = CONTEXT_LINKS.find((x) => x.id === l)!;
                            return (
                              <span key={l} className="flex items-center gap-1 rounded-full border border-app bg-surface-2/40 px-2 py-1 text-[11px] text-app-1">
                                <c.icon size={11} /> {c.label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-app px-5 py-3">
                <button
                  onClick={() => (step === 0 ? close() : setStep(step - 1))}
                  className="flex items-center gap-1 rounded-lg border border-app px-3 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong"
                >
                  <ChevronLeft size={14} /> {step === 0 ? "Cancel" : "Back"}
                </button>
                <button
                  onClick={() => (last ? finish() : setStep(step + 1))}
                  disabled={!stepValid(stepKey)}
                  className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-black transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ background: accent }}
                >
                  {last ? (
                    <>
                      <Check size={14} /> Create Workspace
                    </>
                  ) : (
                    <>
                      Continue <ChevronRight size={14} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
